/**
 * VENTISETTE — ATELIER MOTION ENGINE
 *
 * Vanilla, dependency-free, ~5 KB. Loaded with `defer` so it never blocks
 * rendering. Every effect degrades to "content is simply visible" when
 * JavaScript is unavailable or the visitor prefers reduced motion.
 */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  root.classList.add('ven-js');

  /* ----------------------------------------------------------------------
   * Utilities
   * -------------------------------------------------------------------- */

  /** rAF-throttled scroll subscriber. One listener for the whole page. */
  const scrollSubscribers = new Set();
  let scrollTicking = false;

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY || window.pageYOffset;
      scrollSubscribers.forEach((fn) => fn(y));
      scrollTicking = false;
    });
  }

  function subscribeScroll(fn) {
    scrollSubscribers.add(fn);
    fn(window.scrollY || window.pageYOffset);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /* ----------------------------------------------------------------------
   * 1. Scroll reveals
   *
   * A single IntersectionObserver for every [data-ven-reveal] on the page.
   * Elements inside a [data-ven-stagger] parent inherit an incremental delay.
   * -------------------------------------------------------------------- */
  function initReveals() {
    const targets = document.querySelectorAll('[data-ven-reveal], .ven-mask, .ven-lines');
    if (!targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    // Assign stagger delays up front so the observer stays cheap.
    document.querySelectorAll('[data-ven-stagger]').forEach((group) => {
      const step = parseInt(group.dataset.venStagger, 10) || 90;
      const kids = group.querySelectorAll(':scope > [data-ven-reveal], :scope > * > [data-ven-reveal]');
      kids.forEach((kid, i) => {
        kid.style.setProperty('--ven-delay', `${i * step}ms`);
      });
    });

    // Split headline text into masked lines. Done after layout so the browser's
    // own line-breaking decides where the masks fall.
    document.querySelectorAll('.ven-lines[data-ven-split]').forEach(splitLines);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => observer.observe(el));
  }

  /**
   * Wraps each rendered line of text in an overflow-hidden mask so it can
   * rise into place. Falls back silently on any unexpected markup.
   */
  function splitLines(el) {
    const text = el.textContent.trim();
    if (!text || el.querySelector('.ven-line')) return;

    const words = text.split(/\s+/);
    el.textContent = '';

    // Measure: lay every word out in its own inline span, then group spans
    // that share a vertical offset into one line.
    const probes = words.map((word) => {
      const span = document.createElement('span');
      span.textContent = word;
      span.style.display = 'inline-block';
      el.append(span, document.createTextNode(' '));
      return span;
    });

    const lines = [];
    let currentTop = null;
    probes.forEach((probe, i) => {
      const top = probe.offsetTop;
      if (currentTop === null || Math.abs(top - currentTop) > 2) {
        currentTop = top;
        lines.push([]);
      }
      lines[lines.length - 1].push(words[i]);
    });

    el.textContent = '';
    const step = parseInt(el.dataset.venSplit, 10) || 90;
    lines.forEach((line, i) => {
      const outer = document.createElement('span');
      outer.className = 'ven-line';
      const inner = document.createElement('span');
      inner.textContent = line.join(' ');
      inner.style.setProperty('--ven-delay', `${i * step}ms`);
      outer.appendChild(inner);
      el.appendChild(outer);
    });
  }

  /* ----------------------------------------------------------------------
   * 2. Parallax
   *
   * Transform-only, so it stays on the compositor. Elements are tracked
   * lazily: we only run the maths while they are on screen.
   * -------------------------------------------------------------------- */
  function initParallax() {
    const items = document.querySelectorAll('[data-ven-parallax]');
    if (!items.length || reduced.matches || !('IntersectionObserver' in window)) return;

    const active = new Set();

    const visibility = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
        });
      },
      { rootMargin: '15% 0px' }
    );

    items.forEach((el) => visibility.observe(el));

    subscribeScroll(() => {
      if (!active.size) return;
      const vh = window.innerHeight;
      active.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // -1 when the element is just below the fold, +1 when just above it.
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
        const strength = parseFloat(el.dataset.venParallax) || 0.12;
        const shift = -progress * strength * 100;
        el.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
      });
    });
  }

  /* ----------------------------------------------------------------------
   * 3. Scroll progress hairline
   * -------------------------------------------------------------------- */
  function initProgress() {
    const bar = document.querySelector('.ven-progress');
    if (!bar || reduced.matches) return;

    subscribeScroll((y) => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(1, Math.max(0, y / max)) : 0;
      bar.style.setProperty('--ven-progress', ratio.toFixed(4));
      bar.classList.toggle('is-visible', y > 200);
    });
  }

  /* ----------------------------------------------------------------------
   * 4. Header state
   *
   * We only toggle a class on <body>. All visual work happens in CSS, and
   * Horizon's own sticky-header logic is left completely untouched.
   * -------------------------------------------------------------------- */
  function initHeader() {
    let scrolled = false;
    subscribeScroll((y) => {
      const next = y > 24;
      if (next === scrolled) return;
      scrolled = next;
      document.body.classList.toggle('ven-scrolled', next);
    });
  }

  /* ----------------------------------------------------------------------
   * 5. Button micro-interaction
   *
   * A single ripple element per press, positioned at the pointer and removed
   * when its animation ends. Pointer events only — keyboard activation keeps
   * the quieter focus ring.
   * -------------------------------------------------------------------- */
  function initRipple() {
    if (reduced.matches) return;

    document.addEventListener(
      'pointerdown',
      (event) => {
        const btn = event.target.closest('.ven-btn');
        if (!btn) return;

        const rect = btn.getBoundingClientRect();
        const ripple = document.createElement('span');
        const size = Math.max(rect.width, rect.height) * 2;

        ripple.style.cssText = `position:absolute;z-index:-1;border-radius:50%;pointer-events:none;
          width:${size}px;height:${size}px;
          left:${event.clientX - rect.left - size / 2}px;
          top:${event.clientY - rect.top - size / 2}px;
          background:currentColor;opacity:.14;transform:scale(0);
          transition:transform 620ms cubic-bezier(.16,1,.3,1),opacity 620ms linear`;

        btn.appendChild(ripple);
        requestAnimationFrame(() => {
          ripple.style.transform = 'scale(1)';
          ripple.style.opacity = '0';
        });
        setTimeout(() => ripple.remove(), 700);
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------------------
   * 6. Hover image swap
   *
   * Second product image is only fetched once the visitor shows intent, so
   * the initial page weight stays low.
   * -------------------------------------------------------------------- */
  function initLazyHoverImages() {
    const swap = (event) => {
      const alt = event.currentTarget.querySelector('img[data-ven-src]');
      if (!alt) return;
      alt.src = alt.dataset.venSrc;
      if (alt.dataset.venSrcset) alt.srcset = alt.dataset.venSrcset;
      delete alt.dataset.venSrc;
    };

    document.querySelectorAll('.ven-product, .ven-card').forEach((el) => {
      el.addEventListener('pointerenter', swap, { once: true });
      el.addEventListener('focusin', swap, { once: true });
    });
  }

  /* ----------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------- */
  function init() {
    initReveals();
    initParallax();
    initProgress();
    initHeader();
    initRipple();
    initLazyHoverImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-run inside the theme editor, where sections are swapped in live.
  document.addEventListener('shopify:section:load', () => {
    initReveals();
    initParallax();
    initLazyHoverImages();
  });
})();
